import { BrowserModule, DomSanitizer } from '@angular/platform-browser';
import { NgModule, NO_ERRORS_SCHEMA } from '@angular/core';
import { RouterModule } from '@angular/router';
import { PathLocationStrategy, LocationStrategy } from '@angular/common';
import { ReactiveFormsModule, FormsModule } from '@angular/forms';
import { HttpClient, HttpClientModule, HTTP_INTERCEPTORS } from '@angular/common/http';

import { AppRoutingModule } from './app-routing.module';

import { environment } from 'src/environments/environment';

// Lib
import { TranslateLoader, TranslateModule } from '@ngx-translate/core';
import { TranslateHttpLoader, TRANSLATE_HTTP_LOADER_CONFIG } from '@ngx-translate/http-loader';
import { NgProgressModule } from 'ngx-progressbar';
import { NgxMaskDirective, NgxMaskPipe, provideNgxMask } from 'ngx-mask';
import { NgbActiveModal, NgbDateParserFormatter, NgbModule } from '@ng-bootstrap/ng-bootstrap';
import { NgSelectModule } from '@ng-select/ng-select';
import { DragulaService } from 'ng2-dragula';

import { WebsocketModule } from './websocket';

import { OcticonDirective } from './directives/octicon.directive';

import { AppComponent } from './app.component';
import { ApiInterceptor } from './interceptors/api-interceptor';

import { ApiService } from './services/api.service';
import { AuthService, LanguageService } from './services';

import {
  DashboardComponent,
  HelpComponent,
  LoginComponent,
  NoAccessComponent,
  NotFoundComponent,
  PagesComponent,
  RegistrationComponent,
  UserProfileComponent,
  UserEditComponent,
  OrganizationListComponent,
  OrganizationProfileComponent,
  OrganizationEditComponent,
  OrganizationCreateComponent,
  OrganizationBasicComponent,
  OrganizationEmployeesComponent,
  ExportComponent,
  ImportComponent,
  AttributesListComponent,
  FilesListComponent,
  FileCreateComponent,
  ProductsListComponent,
  ProductCreateComponent,
  ProductEditComponent,
  ProductProfileComponent,
  AddinfoComponent,
  AddinfoProfileComponent,
  KaspiPriceCreateComponent,
  KaspiPriceEditComponent,
  KaspiPriceListComponent,
  IntegrationComponent,
  KaspiSettingsComponent
} from './components/pages';

import {
  ClosedPageLayoutComponent,
  OpenPageLayoutComponent,
  BackgroundComponent,
  HeaderForOpenPagesComponent,
  ErrorPageComponent,
  FfInputLangComponent,
  FfTextareaLangComponent,
  FfButtonUploadFilesComponent,
  PaginationComponent,
  NotificationComponent,
  ChangeStateComponent
} from './components/layout';

import {
  EmployeeInvitationComponent,
  FileUploadProcessComponent,
  ImagesComponent,
  AttachingCertificateComponent,
  ReasonForRejectComponent
} from './components/dialog';
import { UserOrgRolesComponent } from './components/pages/roles/user-org-roles/user-org-roles.component';
import { SettingsComponent } from './components/pages/settings/settings.component';
import { UserOrgRolesActionsComponent } from './components/dialog/roles/user-org-roles-actions/user-org-roles-actions.component';
import { DownloadComponent } from './components/pages/integrations/kaspi/settings/kaspi-settings/download/download.component';
import { UploadComponent } from './components/pages/integrations/kaspi/settings/kaspi-settings/upload/upload.component';
import { OrgRoleEditComponent } from './components/dialog/organization/org-role-edit/org-role-edit.component';
import { AttributesActionsComponent } from './components/dialog/attributes/attributes-actions/attributes-actions.component';
import { PresetCreateComponent } from './components/pages/presets/preset-create/preset-create.component';
import { PresetEditComponent } from './components/pages/presets/preset-edit/preset-edit.component';
import { PresetElementsListComponent } from './components/pages/presets/preset-elements-list/preset-elements-list.component';
import { PresetElementEditComponent } from './components/pages/presets/preset-element-edit/preset-element-edit.component';
import { PresetElementCreateComponent } from './components/pages/presets/preset-element-create/preset-element-create.component';
import { NotificationsListComponent } from './components/pages/notifications/notifications-list/notifications-list.component';
import { NotificationProfileComponent } from './components/pages/notifications/notification-profile/notification-profile.component';
import { NotificationsRolesSettingsComponent } from './components/pages/notifications/notifications-settings/notifications-roles-settings/notifications-roles-settings.component';
import { NotificationsChannelSettingsComponent } from './components/pages/notifications/notifications-settings/notifications-channel-settings/notifications-channel-settings.component';
import { NotificationsRolesSettingsActionsComponent } from './components/dialog/notifications/notifications-roles-settings-actions/notifications-roles-settings-actions.component';
import { NotificationsChannelsSettingsActionsComponent } from './components/dialog/notifications/notifications-channels-settings-actions/notifications-channels-settings-actions.component';
import { PresetsListComponent } from './components/pages/presets/presets-list/presets-list.component';
import { PresetAddElementActionsComponent } from './components/dialog/presets/preset-add-element-actions/preset-add-element-actions.component';
import { PublicApiComponent } from './components/pages/integrations/public-api/public-api.component';
import { OzonSettingsComponent } from './components/pages/integrations/ozon/settings/ozon-settings/ozon-settings.component';
import { OzonProductListComponent } from './components/pages/integrations/ozon/ozon-product-list/ozon-product-list.component';
import { OzonSendProductsComponent } from './components/pages/integrations/ozon/ozon-send-products/ozon-send-products.component';
import { OrgUserProfileComponent } from './components/dialog/organization/org-user-profile/org-user-profile.component';
import { EmployeePositionsComponent } from './components/pages/organizations/employee-positions/employee-positions.component';
import { DepartmentsListComponent } from './components/pages/organizations/departments/departments-list/departments-list.component';
import { OrgEmployeePositionActionsComponent } from './components/dialog/organization/org-employee-position-actions/org-employee-position-actions.component';
import { OrgDepartmentsActionsComponent } from './components/dialog/organization/org-departments-actions/org-departments-actions.component';
import { StructuresListComponent } from './components/pages/organizations/structures/structures-list/structures-list.component';
import { ViewFileDataComponent } from './components/dialog/files/view-file-data/view-file-data.component';
import { SafeurlPipe } from './safe-url.pipe';
import { OrgStructrureActionsComponent } from './components/dialog/organization/org-structrure-actions/org-structrure-actions.component';
import { ContextMenuComponent } from './components/layout/forms/context-menu/context-menu.component';
import { AttributeModelForProductTableComponent } from './components/dialog/products/attribute-model-for-product-table/attribute-model-for-product-table.component';

import 'hammerjs';
import 'mousetrap';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { AccsessToAttributesComponent } from './components/dialog/attributes/accsess-to-attributes/accsess-to-attributes.component';
import { ProductsAttributesEditComponent } from './components/dialog/attributes/products-attributes-edit/products-attributes-edit.component';
import { EditableContentComponent } from './components/layout/forms/editable-content/editable-content.component';
import { ConfirmActionComponent } from './components/dialog/confirm/confirm-action/confirm-action.component';
import { CategoriesActionsComponent } from './components/dialog/categories/categories-actions/categories-actions.component';
import { ProductsDataDownloadComponent } from './components/dialog/files/products-data-download/products-data-download.component';
import { ProductsDownloadWithSettingsComponent } from './components/pages/products/products-downloads/products-download-with-settings/products-download-with-settings.component';
import { ProductsDownloadHistoryComponent } from './components/pages/products/products-downloads/products-download-history/products-download-history.component';
import { MarkingSettingsComponent } from './components/pages/integrations/marking/settings/marking-settings/marking-settings.component';
import { MarkingProductListComponent } from './components/pages/integrations/marking/products/marking-product-list/marking-product-list.component';
import { MarkingProductProfileComponent } from './components/dialog/integrations/marking-product-profile/marking-product-profile.component';
import { MarkingProductCreateComponent } from './components/dialog/integrations/marking-product-create/marking-product-create.component';
import { AccessToCategoriesComponent } from './components/dialog/categories/access-to-categories/access-to-categories.component';
import { TreeViewComponent } from './components/layout/forms/tree-view/tree-view.component';
import { EdsComponent } from './components/dialog/eds/eds.component';
import { TagsActionsComponent } from './components/dialog/tags/tags-actions/tags-actions.component';
import { TagsDialogManagmentComponent } from './components/dialog/tags/tags-dialog-managment/tags-dialog-managment.component';
import { ColorPickerModule } from 'ngx-color-picker';
import { TagsListComponent } from './components/dialog/tags/tags-list/tags-list.component';
import { AttributesValidatorsActionsComponent } from './components/dialog/attributes/attributes-validators-actions/attributes-validators-actions.component';
import { AttributesValidatorListComponent } from './components/pages/attributes/attributes-validator/attributes-validator-list/attributes-validator-list.component';
import { OzonProductProfileComponent } from './components/dialog/integrations/ozon/ozon-product-profile/ozon-product-profile.component';
import { SubscriptionsListComponent } from './components/pages/subscriptions/subscriptions-list/subscriptions-list.component';
import { SubscriptionsActionsComponent } from './components/dialog/subscriptions/subscriptions-actions/subscriptions-actions.component';
import { SubscriptionsCreateComponent } from './components/pages/subscriptions/subscriptions-create/subscriptions-create.component';
import { ReferencesListComponent } from './components/pages/references/references-list/references-list.component';
import { RereferencesActionsComponent } from './components/dialog/references/rereferences-actions/rereferences-actions.component';
import { AttributesGroupsListComponent } from './components/pages/attributes/attributes-groups-list/attributes-groups-list.component';
import { AttributesGroupsActionsComponent } from './components/dialog/attributes/attributes-groups-actions/attributes-groups-actions.component';
import { ProductDataUploadComponent } from './components/dialog/files/product-data-upload/product-data-upload.component';
import { AttributesMappingActionsComponent } from './components/dialog/attributes/mapping/attributes-mapping-actions/attributes-mapping-actions.component';
import { AttributesMappingListComponent } from './components/dialog/attributes/mapping/attributes-mapping-list/attributes-mapping-list.component';
import { AttributesMappingManagmentComponent } from './components/dialog/attributes/mapping/attributes-mapping-managment/attributes-mapping-managment.component';
import { ConditionsFilterComponent } from './components/layout/forms/conditions-filter/conditions-filter.component';
import { FileToCategoryComponent } from './components/dialog/files/file-to-category/file-to-category.component';
import { FfActionsMenuComponent } from './components/layout/forms/ff-actions-menu/ff-actions-menu.component';
import { FileInfoComponent } from './components/pages/files/file-info/file-info.component';
import { FileEditComponent } from './components/pages/files/file-edit/file-edit.component';
import { AccessToFileCategoriesComponent } from './components/dialog/categories/access-to-file-categories/access-to-file-categories.component';
import { UserOrgRolesControlsComponent } from './components/dialog/roles/user-org-roles-controls/user-org-roles-controls.component';
import { OrgsIntegrationComponent } from './components/pages/integrations/orgs-integration/orgs-integration.component';
import { DndDirective } from './derectives/dnd.directive';
import { SpecificationsListComponent } from './components/pages/specifications/specifications-list/specifications-list.component';
import { SpecificationsCreateComponent } from './components/pages/specifications/specifications-create/specifications-create.component';
import { SpecificationsEditComponent } from './components/pages/specifications/specifications-edit/specifications-edit.component';
import { SpecificationsFormulaSettingsComponent } from './components/dialog/specifications/specifications-formula-settings/specifications-formula-settings.component';
import { SpecificationsDataDownloadComponent } from './components/dialog/products/specifications-data-download/specifications-data-download.component';
import { ProductsDownloadWithSpecificationsComponent } from './components/pages/products/products-downloads/products-download-with-specifications/products-download-with-specifications.component';
import { ProductsDownloadSpecificationsHistoryComponent } from './components/pages/products/products-downloads/products-download-specifications-history/products-download-specifications-history.component';
import { FilterFromAttributeComponent } from './components/dialog/attributes/filter-from-attribute/filter-from-attribute.component';
import { AttributeDeletionConfirmationComponent } from './components/dialog/attributes/attribute-deletion-confirmation/attribute-deletion-confirmation.component';
import { ConfirmEmailComponent } from './components/dialog/registration/confirm-email/confirm-email.component';
import { DeletingAttributeGroupComponent } from './components/dialog/attributes/deleting-attribute-group/deleting-attribute-group.component';
import { DeletingRoleComponent } from './components/dialog/roles/deleting-role/deleting-role.component';
import { customFormatter } from './services/custom-formatter.service';
import { DirectoryDeleteComponent } from './components/dialog/references/directory-delete/directory-delete.component';
import { ForgotPasswordComponent } from './components/dialog/registration/authentication/forgot-password/forgot-password.component';
import { UploadFileWithDateComponent } from './components/dialog/files/upload-file-with-date/upload-file-with-date.component';
import { DeleteFileComponent } from './components/dialog/files/delete-file/delete-file.component';
import { DonwloadFileFromStorageComponent } from './components/dialog/files/donwload-file-from-storage/donwload-file-from-storage.component';
import { CustomersIntegrationComponent } from './components/dialog/products/customers-integration/customers-integration.component';
import { AngularSvgIconModule } from 'angular-svg-icon';
import { DatePickerCustomViewControlComponent } from './components/layout/forms/date-picker-custom-view-control/date-picker-custom-view-control.component';
// import { NgToggleModule } from 'ng-toggle-button';




export const options = {};

@NgModule({
  declarations: [
    AppComponent,
    NoAccessComponent,
    NotFoundComponent,
    PagesComponent,
    PaginationComponent,
    DashboardComponent,
    LoginComponent,
    HelpComponent,
    OpenPageLayoutComponent,
    ClosedPageLayoutComponent,
    RegistrationComponent,
    BackgroundComponent,
    HeaderForOpenPagesComponent,
    OcticonDirective,
    ErrorPageComponent,
    UserProfileComponent,
    UserEditComponent,
    FfInputLangComponent,
    FfTextareaLangComponent,
    OrganizationListComponent,
    OrganizationProfileComponent,
    OrganizationEditComponent,
    OrganizationCreateComponent,
    OrganizationBasicComponent,
    OrganizationEmployeesComponent,
    NotificationComponent,
    EmployeeInvitationComponent,
    ExportComponent,
    ImportComponent,
    FileUploadProcessComponent,
    FfButtonUploadFilesComponent,
    FilesListComponent,
    FileCreateComponent,
    ProductsListComponent,
    ProductCreateComponent,
    ProductEditComponent,
    ProductProfileComponent,
    AddinfoComponent,
    AddinfoProfileComponent,
    AttributesListComponent,
    ImagesComponent,
    AttachingCertificateComponent,
    ChangeStateComponent,
    ReasonForRejectComponent,
    KaspiPriceCreateComponent,
    KaspiPriceEditComponent,
    KaspiPriceListComponent,
    IntegrationComponent,
    KaspiSettingsComponent,
    UserOrgRolesComponent,
    SettingsComponent,
    UserOrgRolesActionsComponent,
    UploadComponent,
    DownloadComponent,
    OrgRoleEditComponent,
    AttributesActionsComponent,
    PresetCreateComponent,
    PresetEditComponent,
    PresetElementsListComponent,
    PresetElementEditComponent,
    PresetElementCreateComponent,
    NotificationsListComponent,
    NotificationProfileComponent,
    NotificationsRolesSettingsComponent,
    NotificationsChannelSettingsComponent,
    NotificationsRolesSettingsActionsComponent,
    NotificationsChannelsSettingsActionsComponent,
    PresetsListComponent,
    PresetAddElementActionsComponent,
    PublicApiComponent,
    OzonSettingsComponent,
    OzonProductListComponent,
    OzonSendProductsComponent,
    OrgUserProfileComponent,
    EmployeePositionsComponent,
    DepartmentsListComponent,
    OrgEmployeePositionActionsComponent,
    OrgDepartmentsActionsComponent,
    StructuresListComponent,
    ViewFileDataComponent,
    SafeurlPipe,
    OrgStructrureActionsComponent,
    ContextMenuComponent,
    AttributeModelForProductTableComponent,
    AccsessToAttributesComponent,
    ProductsAttributesEditComponent,
    EditableContentComponent,
    ConfirmActionComponent,
    CategoriesActionsComponent,
    ProductsDataDownloadComponent,
    ProductsDownloadHistoryComponent,
    ProductsDownloadWithSettingsComponent,
    MarkingSettingsComponent,
    MarkingProductListComponent,
    MarkingProductProfileComponent,
    MarkingProductCreateComponent,
    AccessToCategoriesComponent,
    TreeViewComponent,
    EdsComponent,
    TagsActionsComponent,
    TagsDialogManagmentComponent,
    TagsListComponent,
    AttributesValidatorsActionsComponent,
    AttributesValidatorListComponent,
    OzonProductProfileComponent,
    SubscriptionsListComponent,
    SubscriptionsActionsComponent,
    SubscriptionsCreateComponent,
    ReferencesListComponent,
    RereferencesActionsComponent,
    AttributesGroupsListComponent,
    AttributesGroupsActionsComponent,
    ProductDataUploadComponent,
    AttributesMappingManagmentComponent,
    AttributesMappingActionsComponent,
    AttributesMappingListComponent,
    ConditionsFilterComponent,
    FileToCategoryComponent,
    FfActionsMenuComponent,
    FileInfoComponent,
    FileEditComponent,
    AccessToFileCategoriesComponent,
    UserOrgRolesControlsComponent,
    OrgsIntegrationComponent,
    DndDirective,
    SpecificationsListComponent,
    SpecificationsCreateComponent,
    SpecificationsEditComponent,
    SpecificationsFormulaSettingsComponent,
    SpecificationsDataDownloadComponent,
    ProductsDownloadWithSpecificationsComponent,
    ProductsDownloadSpecificationsHistoryComponent,
    FilterFromAttributeComponent,
    AttributeDeletionConfirmationComponent,
    DeletingAttributeGroupComponent,
    DeletingRoleComponent,
    ConfirmEmailComponent,
    ForgotPasswordComponent,
    DirectoryDeleteComponent,
    UploadFileWithDateComponent,
    DeleteFileComponent,
    DonwloadFileFromStorageComponent,
    CustomersIntegrationComponent,
    DatePickerCustomViewControlComponent,
    // NgbdSortableHeader
  ],
  imports: [
    // NgToggleModule.forRoot(),
    BrowserModule,
    HttpClientModule,
    AppRoutingModule,
    FormsModule,
    RouterModule,
    NgProgressModule.withConfig({
      fixed:false,
      meteor:false
    }),
    NgbModule,
    NgSelectModule,
    BrowserAnimationsModule,
    FormsModule,
    ReactiveFormsModule,
    ColorPickerModule,
    AngularSvgIconModule.forRoot(),
    ReactiveFormsModule.withConfig({ warnOnNgModelWithFormControl: 'never' }),
    NgxMaskDirective,
    NgxMaskPipe,
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useClass: TranslateHttpLoader
      }
    }),
    WebsocketModule.config({
      url: environment.ws
    }),
  ],
  providers: [
    ApiService,
    AuthService,
    NgbActiveModal,
    DragulaService,
    { provide: NgbDateParserFormatter,
      useClass: customFormatter 
    },
    provideNgxMask(options),
    {
      provide: TRANSLATE_HTTP_LOADER_CONFIG,
      useValue: {
        prefix: '/assets/i18n/',
        suffix: '.json'
      }
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: ApiInterceptor,
      multi: true
    },
    {
      provide: LocationStrategy,
      useClass: PathLocationStrategy
    },
  ],
  schemas: [NO_ERRORS_SCHEMA],
  bootstrap: [AppComponent]
})

export class AppModule {
  constructor(
    private langService: LanguageService
  ) {
    this.langService.getLanguages();
  }
}

